import { useMemo } from 'react';

import { useActiveUserClasses } from 'src/hooks/use-active-user-classes';
import { useUserCustomGroups } from 'src/hooks/use-user-custom-groups';
import AuthService from 'src/services/api/auth.service';
import { useSelectedClassSelector } from 'src/store/features';

/**
 * Returns list of user custom groups business keys needed for assignment queries
 * */
export const useUserCustomGroupsBks = () => {
  const { activeUserClasses } = useActiveUserClasses();
  const selectedClass = useSelectedClassSelector();
  const isTeacher = AuthService.isTeacher();

  const {
    data: userCustomGroups,
    isError: isUserCustomGroupsError,
    isLoadingWithoutError: isUserCustomGroupsLoading,
  } = useUserCustomGroups();

  return useMemo(() => {
    if (isUserCustomGroupsError) return [];

    // return undefined to skip related assignment queries when not initialized
    if (!activeUserClasses || selectedClass === undefined) return undefined;

    // Handle users with no classes
    if (selectedClass === null) {
      // For teachers: wait for custom groups query to complete before making assignment query
      // For students: custom groups query is skipped, so proceed immediately
      if (isTeacher && isUserCustomGroupsLoading) {
        return undefined; // Skip assignment query while loading
      }

      // Teacher with custom groups: return group IDs to send query
      if (userCustomGroups?.length) {
        return userCustomGroups.map((group) => group.id);
      }

      // Teacher with no groups (userCustomGroups is []): skip assignment query
      if (
        isTeacher &&
        userCustomGroups !== undefined &&
        userCustomGroups.length === 0
      ) {
        return undefined;
      }

      // Student: allow assignment query (students don't have custom groups)
      return [];
    }

    const isSingleClassSelected =
      activeUserClasses.length > 1 && selectedClass.length === 1;

    if (!userCustomGroups?.length || isSingleClassSelected) return [];

    return userCustomGroups.map((group) => group.id);
  }, [
    userCustomGroups,
    activeUserClasses,
    selectedClass,
    isUserCustomGroupsError,
    isUserCustomGroupsLoading,
    isTeacher,
  ]);
};
